import { useCallback, useMemo, useState } from 'react';

import { type NotificationFilter } from '@air/api/types';
import { type NotificationsFilter } from '@air/component-notifications';

import { useGetNotificationSummary } from '~/components/AirNotifications/hooks/useGetNotificationSummary';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

type NotificationsPanelTab = 'all' | 'news' | NotificationFilter;

const isNewsPanelTab = (tab: NotificationsPanelTab): boolean => tab === 'news';

const getNotificationFilterForPanelTab = (tab: NotificationsPanelTab): NotificationFilter | undefined => {
  if (tab === 'all' || tab === 'news') {
    return undefined;
  }

  return tab;
};

const getPanelTabFromFilterId = (filterId: string): NotificationsPanelTab => {
  switch (filterId) {
    case 'all':
      return 'all';
    case 'news':
      return 'news';
    case 'mentions':
    case 'following':
    case 'shortUrl':
      return filterId;
    default:
      return 'all';
  }
};

const useNotificationsPanelFilters = (activeTab: NotificationsPanelTab): NotificationsFilter[] => {
  const { announcementsEnabled } = useTemporaryFeatureFlags();
  const { data: notificationSummary } = useGetNotificationSummary();

  return useMemo(() => {
    const filters: NotificationsFilter[] = [
      {
        count: notificationSummary?.unread?.all,
        id: 'all',
        isActive: activeTab === 'all',
        name: 'All',
      },
      {
        count: notificationSummary?.unread?.mentions,
        id: 'mentions',
        isActive: activeTab === 'mentions',
        name: 'Mentions',
      },
      {
        count: notificationSummary?.unread?.following,
        isActive: activeTab === 'following',
        id: 'following',
        name: 'Following',
      },
      {
        count: notificationSummary?.unread?.shortUrl,
        id: 'shortUrl',
        isActive: activeTab === 'shortUrl',
        name: 'Links',
      },
    ];

    if (announcementsEnabled) {
      filters.push({
        id: 'news',
        isActive: activeTab === 'news',
        name: 'News',
      });
    }

    return filters;
  }, [activeTab, announcementsEnabled, notificationSummary?.unread]);
};

export const useNotificationsPanelTab = () => {
  const [selectedTab, setSelectedTab] = useState<NotificationsPanelTab>('all');
  const { announcementsEnabled } = useTemporaryFeatureFlags();
  // The flag can turn off while News is selected; falling back keeps cached announcements from rendering.
  const activeTab = selectedTab === 'news' && !announcementsEnabled ? 'all' : selectedTab;
  const filters = useNotificationsPanelFilters(activeTab);

  const onFilterClick = useCallback((filter: { id: string }) => {
    setSelectedTab(getPanelTabFromFilterId(filter.id));
  }, []);

  return {
    filters,
    isNewsTab: isNewsPanelTab(activeTab),
    notificationFilter: getNotificationFilterForPanelTab(activeTab),
    onFilterClick,
  };
};
